/**
 * Каталог пряников — API-клиент.
 *
 * Параметры те же, что у `GET /products/catalog` (поиск, фильтры, сортировка,
 * пагинация, `marketplace`, теги, `articles`), плюс `kind`, `files`,
 * `shapeMode`. Ответ — формат каталога товаров, у каждой строки блок
 * `gingerbread`. Выгрузки принимают те же параметры; `articles` — явный список.
 */

import { api } from '../../shared/api/client'
import { createIdempotencyKey } from '../layout-generator/api'
import type { ProductFacets } from '../products-v2/types'
import type { ProductsV2ListParams } from '../products-v2/api'
import { toGingerbreadVisuals, toVisualsJobId, type GingerbreadVisuals } from './visuals'
import { toKitScene, toSceneQuote, toSceneStart, type KitScene, type SceneQuote } from './scene'
import type {
  GingerbreadFilesFilter,
  GingerbreadKindFilter,
  GingerbreadRow,
  GingerbreadShapeMode,
  NoProductParams,
  NoProductRow,
  Pp2BackfillJob,
  Pp2BackfillStart,
  Pp2BackfillStatus,
} from './types'

/** Гейтвей отдаёт { success, data } — разворачиваем до data. */
const unwrap = (r: any) => r.data?.data ?? r.data

export interface GingerbreadFilterParams
  extends Omit<ProductsV2ListParams, 'limit' | 'offset' | 'sortBy' | 'sortOrder'> {
  kind?: GingerbreadKindFilter
  files?: GingerbreadFilesFilter
  shapeMode?: GingerbreadShapeMode
  /** Есть карточка ПИМ в marketplace_cards: любая площадка, любой статус. */
  hasCard?: 'true' | 'false'
}

export interface GingerbreadListParams extends GingerbreadFilterParams {
  limit: number
  offset: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

/** Строка вкладки «Без товара»: числа из БД приходят и строками, и числами. */
function toNoProductRow(raw: any): NoProductRow {
  const text = (value: unknown): string | null => (value == null || value === '' ? null : String(value))
  const num = (value: unknown): number | null => {
    if (value == null || value === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return {
    gingerbreadId: String(raw?.gingerbreadId ?? ''),
    jobId: text(raw?.jobId),
    jobName: text(raw?.jobName),
    name: text(raw?.name),
    designCode: text(raw?.designCode),
    articleReserved: text(raw?.articleReserved),
    shapeMode: raw?.shapeMode === 'unified' || raw?.shapeMode === 'free' ? raw.shapeMode : null,
    shapeName: text(raw?.shapeName),
    widthMm: num(raw?.widthMm),
    heightMm: num(raw?.heightMm),
    doughWeightG: num(raw?.doughWeightG),
    textOnImage: text(raw?.textOnImage),
    createdAt: text(raw?.createdAt),
  }
}

/** Состояние разового импорта PP2: числа и списки приходят от бэкенда, но не доверяем их форме. */
export function toPp2BackfillJob(raw: any): Pp2BackfillJob {
  const num = (value: unknown): number => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  const text = (value: unknown): string | null => (value == null || value === '' ? null : String(value))
  // product-service отдаёт `completed | failed | interrupted | running` и счётчики `processed`/`total` верхнего уровня;
  // `done` и `progress` — прежняя форма контракта, оставлена на случай старого бэкенда.
  const rawStatus = String(raw?.status ?? '')
  const status: Pp2BackfillStatus = rawStatus === 'completed' || rawStatus === 'done'
    ? 'done'
    : rawStatus === 'failed' || rawStatus === 'interrupted' ? 'failed' : 'running'
  const result = raw?.result
  return {
    status,
    progress: {
      done: num(raw?.progress?.done ?? raw?.processed),
      total: num(raw?.progress?.total ?? raw?.total),
    },
    result: result
      ? {
          created: num(result.created),
          updated: num(result.updated),
          skipped: num(result.skipped),
          errors: (Array.isArray(result.errors) ? result.errors : []).map((item: any) => ({
            designCode: text(item?.designCode),
            code: text(item?.code),
            message: String(item?.message ?? ''),
          })),
          emptyCircles: (Array.isArray(result.emptyCircles) ? result.emptyCircles : []).map((item: any) => ({
            article: String(item?.article ?? ''),
            index: item?.index == null ? null : num(item.index),
          })),
        }
      : null,
  }
}

const BASE = '/gingerbread/catalog'
const KITS = '/gingerbread/kits'

export type GingerbreadExportParams = GingerbreadFilterParams

export const gingerbreadCatalogAPI = {
  /** Страница каталога. Возвращает { products, total }. */
  async getCatalog(
    params: GingerbreadListParams,
    signal?: AbortSignal,
  ): Promise<{ products: GingerbreadRow[]; total: number }> {
    const response = await api.get(BASE, { params, signal })
    const data = unwrap(response) || {}
    const products: GingerbreadRow[] = Array.isArray(data) ? data : data.products || []
    const total = Number(data.pagination?.total ?? data.total)
    return { products, total: Number.isFinite(total) ? total : products.length }
  },

  /** Фасетные счётчики для текущего набора фильтров. */
  async getFacets(params: GingerbreadListParams, signal?: AbortSignal): Promise<ProductFacets> {
    const response = await api.get(`${BASE}/facets`, { params, signal })
    return unwrap(response) || {}
  },

  /** «Раскладка»: CSV сахарной бумаги и теста по наборам. */
  exportRaskladka(params: GingerbreadExportParams) {
    return api.get(`${BASE}/export/raskladka`, { params, responseType: 'blob' })
  },

  /** «Ассортимент»: XLSX с листом «PP1 - пряники». */
  exportAssortment(params: GingerbreadExportParams) {
    return api.get(`${BASE}/export/assortment`, { params, responseType: 'blob' })
  },

  /**
   * «Визуалы и черновики карточек»: слайды МП, SEO и черновики в «Черновиках ПИМ».
   * Публикации на площадки нет. 202 { jobId } — ход задачи идёт по WebSocket фоновых задач.
   */
  async startVisuals(body: { articles?: string[]; jobId?: string }): Promise<{ jobId: string | null }> {
    const response = await api.post(`${BASE}/visuals`, body)
    return { jobId: toVisualsJobId(unwrap(response)) }
  },

  /** Слайды товара, статус визуалов и черновики по площадкам и кабинетам. */
  async getVisuals(article: string, signal?: AbortSignal): Promise<GingerbreadVisuals> {
    const response = await api.get(`${BASE}/visuals/${encodeURIComponent(article)}`, { signal })
    return toGingerbreadVisuals(unwrap(response), article)
  },

  /** Смета сцены «Набор на торте» (право Gingerbread:UPDATE): без `quote` гейтвей только считает, ничего не запускает. */
  async quoteScene(article: string): Promise<SceneQuote> {
    const response = await api.post(`${KITS}/${encodeURIComponent(article)}/scene`, {})
    return toSceneQuote(unwrap(response))
  },

  /** Ключ идемпотентности запуска сцены: один на подтверждённую смету, повтор запроса не заведёт вторую платную операцию. */
  newSceneKey(): string {
    return createIdempotencyKey('gingerbread-scene')
  },

  /** Запуск платной сцены по подписанной смете (право Gingerbread:APPROVE). 202 `{runId, operationId}`. */
  async startScene(article: string, quote: string, idempotencyKey: string): Promise<{ runId: string | null; operationId: string | null }> {
    const response = await api.post(
      `${KITS}/${encodeURIComponent(article)}/scene`,
      { quote },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    )
    return toSceneStart(unwrap(response))
  },

  /** Вкладка «Без товара»: пряники с макетом и файлами, но без товара PP0 (право Gingerbread:READ). */
  async getNoProduct(params: NoProductParams, signal?: AbortSignal): Promise<{ items: NoProductRow[]; total: number }> {
    const response = await api.get(`${BASE}/no-product`, { params, signal })
    const data = unwrap(response) || {}
    const items: NoProductRow[] = (Array.isArray(data) ? data : data.items || []).map(toNoProductRow)
    const total = Number(data.pagination?.total ?? data.total)
    return { items, total: Number.isFinite(total) ? total : items.length }
  },

  /** «Создать товары» для выбранных пряников без товара (право Gingerbread:APPROVE). 202 `{jobId}`. */
  async createProductsForNoProduct(gingerbreadIds: string[]): Promise<{ jobId: string | null }> {
    const response = await api.post(`${BASE}/no-product/create-products`, { gingerbreadIds })
    return { jobId: toVisualsJobId(unwrap(response)) }
  },

  /**
   * Разовый импорт наборов PP2 из «Капк 5» (право Gingerbread:APPROVE). 202 `{jobId, total}`.
   * Повтор безопасен: готовые наборы пропускаются. Задача видна в «Фоновых задачах».
   */
  async startPp2Backfill(body: { designCodes?: string[]; limit?: number } = {}): Promise<Pp2BackfillStart> {
    const response = await api.post('/gingerbread/pp2/backfill', body)
    const data = unwrap(response) || {}
    const jobId = toVisualsJobId(data)
    if (!jobId) throw new Error('Сервер не вернул номер задачи импорта')
    const total = Number(data.total)
    return { jobId, total: Number.isFinite(total) ? total : null }
  },

  /** Ход и итог импорта PP2: опрос раз в несколько секунд. */
  async getPp2Backfill(jobId: string, signal?: AbortSignal): Promise<Pp2BackfillJob> {
    const response = await api.get(`/gingerbread/pp2/backfill/${encodeURIComponent(jobId)}`, { signal })
    return toPp2BackfillJob(unwrap(response))
  },

  /** Состояние сцены набора: нет / рисуется / готова (с превью) / ошибка. */
  async getScene(article: string, signal?: AbortSignal): Promise<KitScene> {
    const response = await api.get(`${KITS}/${encodeURIComponent(article)}/scene`, { signal })
    return toKitScene(unwrap(response))
  },
}
